import React, { useState } from 'react';
import { X, CheckCircle, ChevronRight, ChevronLeft } from 'lucide-react';
import api from '../api';

const LMSFeedbackModal = ({ course, schema, formId, formTitle, initialFeedback, onClose, onSubmitSuccess }) => {
  const [step, setStep] = useState(1);
  const [answers, setAnswers] = useState(() => {
    if (!initialFeedback) return {};
    return {
      ...(initialFeedback.module_feedback || {}),
      ...(initialFeedback.platform_feedback || {})
    };
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Safely extract schema parts
  const moduleQuestions = schema?.module_feedback || [];
  const platformQuestions = [];

  const handleAnswerChange = (qId, value) => {
    setAnswers(prev => ({ ...prev, [qId]: value }));
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      
      const payload = {
        course_id: course.externalContentRootId || course.course_id || course.id,
        form_id: formId || null, // which feedback form this answers (lets admins run multiple forms)
        course_name: formTitle || course.title,
        assignment_id: course.assignment_id || null, // Assuming assignment_id is available
        module_feedback: {},
        platform_feedback: {}
      };

      // Split answers back into module vs platform
      moduleQuestions.forEach(q => {
        if (answers[q.id] !== undefined) payload.module_feedback[q.id] = answers[q.id];
      });
      platformQuestions.forEach(q => {
        if (answers[q.id] !== undefined) payload.platform_feedback[q.id] = answers[q.id];
      });

      await api.post('/student/lms/feedback', payload);
      onSubmitSuccess();
      onClose();
    } catch (err) {
      console.error(err);
      alert("Failed to submit feedback. " + (err.response?.data?.detail || ""));
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderQuestion = (q) => {
    const val = answers[q.id] || '';

    // Check condition
    if (q.condition) {
      const { dependsOn, equals } = q.condition;
      const cleanDependsOn = String(dependsOn || '').trim();
      const actualVal = String(answers[cleanDependsOn] || '').trim().toLowerCase();
      const expectedVals = String(equals || '').toLowerCase().split(',').map(s => s.trim());
      if (!expectedVals.includes(actualVal)) return null;
    }

    let inputElement = null;

    if (q.type === 'text') {
      inputElement = (
        <textarea 
          value={val || ''} 
          onChange={(e) => handleAnswerChange(q.id, e.target.value)}
          placeholder="Type your answer..."
          style={{ width: '100%', minHeight: '80px', padding: '12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-main)', color: 'var(--text-main)', resize: 'vertical' }}
        />
      );
    } else if (q.type === 'choice') {
      inputElement = (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '10px' }}>
          {q.options?.map((opt, idx) => (
            <label key={idx} style={{ 
              display: 'flex', 
              alignItems: 'flex-start', 
              gap: '12px', 
              cursor: 'pointer',
              padding: '16px',
              borderRadius: '12px',
              border: val === opt ? '2px solid var(--primary)' : '1px solid var(--border)',
              background: val === opt ? 'rgba(14, 165, 233, 0.05)' : 'var(--bg-main)',
              transition: 'all 0.2s ease',
              margin: 0,
              width: '100%',
              boxSizing: 'border-box'
            }}>
              <input 
                type="radio" 
                name={`q_${q.id}`} 
                value={opt} 
                checked={val === opt} 
                onChange={() => handleAnswerChange(q.id, opt)}
                style={{ 
                  margin: '2px 0 0 0', 
                  cursor: 'pointer', 
                  width: '18px', 
                  height: '18px',
                  flexShrink: 0,
                  accentColor: 'var(--primary)'
                }}
              />
              <span style={{ 
                color: val === opt ? 'var(--primary)' : 'var(--text-main)', 
                fontSize: '0.95rem',
                lineHeight: '1.4',
                textTransform: 'none',
                letterSpacing: 'normal',
                fontWeight: val === opt ? '600' : '400',
                display: 'block',
                textAlign: 'left'
              }}>
                {opt}
              </span>
            </label>
          ))}
        </div>
      );
    } else if (q.type === 'rating') {
      inputElement = (
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
          {[1, 2, 3, 4, 5].map(num => (
            <button
              key={num}
              onClick={() => handleAnswerChange(q.id, num)}
              style={{
                width: '40px', height: '40px', borderRadius: '50%', border: 'none',
                background: val === num ? 'var(--primary)' : 'var(--bg-main)',
                color: val === num ? '#fff' : 'var(--text-main)',
                cursor: 'pointer', fontWeight: 'bold', fontSize: '1rem',
                boxShadow: val === num ? '0 4px 10px rgba(14, 165, 233, 0.4)' : 'none'
              }}
            >
              {num}
            </button>
          ))}
          <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginLeft: '10px' }}>
            {val ? `Selected: ${val}` : 'Select 1-5'}
          </span>
        </div>
      );
    }

    return (
      <div key={q.id} style={{ marginBottom: '24px', background: 'var(--bg-card)', padding: '20px', borderRadius: '12px', border: '1px solid var(--border)' }}>
        <h4 style={{ margin: '0 0 12px 0', color: 'var(--text-main)', fontSize: '1.05rem', lineHeight: '1.4' }}>
          {q.text} <span style={{ color: '#ef4444', marginLeft: '4px' }}>*</span>
        </h4>
        {inputElement}
      </div>
    );
  };

  const currentQuestions = step === 1 ? moduleQuestions : platformQuestions;
  const isLastStep = step === 2 || platformQuestions.length === 0;

  const isStepComplete = (questions) => {
    for (const q of questions) {
      let isVisible = true;
      if (q.condition) {
        const { dependsOn, equals } = q.condition;
        const cleanDependsOn = String(dependsOn || '').trim();
        const actualVal = String(answers[cleanDependsOn] || '').trim().toLowerCase();
        const expectedVals = String(equals || '').toLowerCase().split(',').map(s => s.trim());
        if (!expectedVals.includes(actualVal)) isVisible = false;
      }
      
      if (isVisible) {
        const val = answers[q.id];
        if (val === undefined || val === null || val === '') {
          return false;
        }
      }
    }
    return true;
  };

  const nextDisabled = !isStepComplete(currentQuestions);
  const submitDisabled = isSubmitting || !isStepComplete(currentQuestions);

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '20px' }}>
      <div style={{ background: 'var(--bg-card)', width: '100%', maxWidth: '700px', maxHeight: '90vh', borderRadius: '16px', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 40px rgba(0,0,0,0.3)', overflow: 'hidden' }}>
        
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--bg-card)' }}>
          <h2 style={{ margin: 0, fontSize: '1.4rem', color: 'var(--text-main)' }}>
            Course Feedback {formTitle ? <span style={{ color: 'var(--text-muted)', fontSize: '1rem', fontWeight: 'normal' }}>- {formTitle}</span> : <span style={{ color: 'var(--text-muted)', fontSize: '1rem', fontWeight: 'normal' }}>- {course.title}</span>}
          </h2>
          <button onClick={onClose} style={{ background: 'var(--bg-main)', border: 'none', cursor: 'pointer', width: '36px', height: '36px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-main)' }}>
            <X size={20} />
          </button>
        </div>

        {/* Progress Bar */}
        {platformQuestions.length > 0 && (
          <div style={{ display: 'flex', borderBottom: '1px solid var(--border)' }}>
            <div style={{ flex: 1, padding: '15px', textAlign: 'center', fontWeight: 'bold', background: step === 1 ? 'var(--primary)' : 'var(--bg-card)', color: step === 1 ? '#fff' : 'var(--text-muted)' }}>
              Step 1: Module Feedback
            </div>
            <div style={{ flex: 1, padding: '15px', textAlign: 'center', fontWeight: 'bold', background: step === 2 ? 'var(--primary)' : 'var(--bg-card)', color: step === 2 ? '#fff' : 'var(--text-muted)' }}>
              Step 2: Platform Feedback
            </div>
          </div>
        )}

        {/* Form Content */}
        <div style={{ padding: '24px', overflowY: 'auto', flex: 1, background: 'var(--bg-main)' }}>
          {currentQuestions.length === 0 ? (
            <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '40px' }}>No questions configured.</p>
          ) : (
            currentQuestions.map(renderQuestion)
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '20px 24px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', background: 'var(--bg-card)' }}>
          {step === 2 ? (
            <button 
              onClick={() => setStep(1)} 
              style={{ padding: '10px 20px', borderRadius: '8px', background: 'var(--bg-main)', color: 'var(--text-main)', border: '1px solid var(--border)', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}
            >
              <ChevronLeft size={18} /> Back
            </button>
          ) : <div></div>}
          
          {!isLastStep ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {nextDisabled && <span style={{ color: '#ef4444', fontSize: '0.85rem' }}>* Please answer all questions to continue</span>}
              <button 
                onClick={() => setStep(2)} 
                disabled={nextDisabled}
                style={{ padding: '10px 24px', borderRadius: '8px', background: nextDisabled ? 'var(--text-muted)' : 'var(--primary)', color: '#fff', border: 'none', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px', cursor: nextDisabled ? 'not-allowed' : 'pointer', opacity: nextDisabled ? 0.6 : 1, transition: 'all 0.2s' }}
              >
                Next <ChevronRight size={18} />
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {submitDisabled && !isSubmitting && <span style={{ color: '#ef4444', fontSize: '0.85rem' }}>* Please answer all questions to submit</span>}
              <button 
                onClick={handleSubmit} 
                disabled={submitDisabled}
                style={{ padding: '10px 24px', borderRadius: '8px', background: submitDisabled ? 'var(--text-muted)' : 'var(--primary)', color: '#fff', border: 'none', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px', cursor: submitDisabled ? 'not-allowed' : 'pointer', opacity: submitDisabled ? 0.6 : 1, transition: 'all 0.2s' }}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Feedback'} <CheckCircle size={18} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LMSFeedbackModal;
